'use client';

import { Stack, Typography, Box } from '@mui/material';
import { DonutBlock, SectionTable } from './TickerPortfolioTabVisuals';
import type { PortfolioTabViewModel } from './TickerPortfolioTabClient';

type Props = {
  data: PortfolioTabViewModel;
  chartColors: string[];
  groupCount: (labels: string[]) => Array<{ label: string; value: number }>;
  groupSum: (items: Array<{ label: string; value: number | null }>) => Array<{ label: string; value: number }>;
  formatOptionalNumber: (value: number | null) => string;
  formatOptionalCurrency: (value: number | null) => string;
  formatOptionalPercent: (value: number | null) => string;
};

export function PortfolioFundsSection({
  data,
  chartColors,
  groupCount,
  groupSum,
  formatOptionalNumber,
  formatOptionalCurrency,
  formatOptionalPercent,
}: Props) {
  if (data.funds.length === 0) {
    return (
      <Typography variant='body2' color='text.secondary'>
        Sem fundos investidos reportados na CVM para o período mais recente.
      </Typography>
    );
  }
  const bySegmentCount = groupCount(data.funds.map((item) => item.segment?.trim() || 'Sem segmento'));
  const bySegmentValue = groupSum(
    data.funds.map((item) => ({
      label: item.segment?.trim() || 'Sem segmento',
      value: item.value,
    })),
  );
  return (
    <Stack spacing={1.5}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
          gap: 1.2,
        }}
      >
        <DonutBlock
          title='Fundos por segmento (qtd.)'
          items={bySegmentCount}
          colors={chartColors}
          infoText='Quantidade de fundos investidos agrupada por segmento.'
        />
        <DonutBlock
          title='Fundos por segmento (valor)'
          items={bySegmentValue}
          colors={chartColors}
          valueKind='currency'
          infoText='Valor investido em fundos agrupado por segmento.'
        />
      </Box>
      <SectionTable
        headers={['Instrument', 'Nome', 'Segmento', 'Quantidade', 'Valor', '% PL']}
        rows={data.funds.map((item) => [
          item.ticker ?? '-',
          item.name,
          item.segment ?? '-',
          formatOptionalNumber(item.quantity),
          formatOptionalCurrency(item.value),
          formatOptionalPercent(item.portfolioPercent),
        ])}
      />
    </Stack>
  );
}
